import { useCallback, useEffect, useRef, useState } from "react";
import { TURNSTILE_SITE_KEY } from "./constants";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: Record<string, unknown>,
      ) => string;
      execute: (widgetId: string) => void;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";
let scriptLoadPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Reset so a later attempt can retry instead of reusing the rejection.
      scriptLoadPromise = null;
      reject(new Error("Failed to load Cloudflare Turnstile script"));
    };
    document.head.appendChild(script);
  });
  return scriptLoadPromise;
}

type PendingSolve = {
  resolve: (token: string) => void;
  reject: (err: Error) => void;
};

/**
 * Invisible Cloudflare Turnstile: the widget never renders on-page. Call
 * `getToken()` right before a request needs one — it resets + executes the
 * widget and resolves with a fresh token. Cloudflare only shows a visible
 * challenge overlay in the rare case it can't verify the visitor silently.
 */
export function useTurnstile() {
  // A state-backed callback ref (rather than a plain useRef) so the widget
  // renders the moment the container actually mounts — the modal this lives
  // in isn't in the DOM until opened, so a mount-once effect would find the
  // container null and never get a second chance to render into it.
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const containerRef = useCallback(
    (node: HTMLDivElement | null) => setContainer(node),
    [],
  );

  const widgetIdRef = useRef<string | undefined>(undefined);
  const readyRef = useRef<Promise<void> | null>(null);
  const pendingRef = useRef<PendingSolve | null>(null);

  useEffect(() => {
    if (!container) return;
    let cancelled = false;

    readyRef.current = loadTurnstileScript().then(() => {
      if (cancelled || !window.turnstile) return;
      widgetIdRef.current = window.turnstile.render(container, {
        sitekey: TURNSTILE_SITE_KEY,
        appearance: "execute",
        execution: "execute",
        callback: (solvedToken: string) => {
          pendingRef.current?.resolve(solvedToken);
          pendingRef.current = null;
        },
        "expired-callback": () => {
          pendingRef.current?.reject(new Error("Captcha challenge expired"));
          pendingRef.current = null;
        },
        "error-callback": () => {
          pendingRef.current?.reject(new Error("Captcha verification failed"));
          pendingRef.current = null;
        },
      });
    });

    return () => {
      cancelled = true;
      if (widgetIdRef.current) window.turnstile?.remove(widgetIdRef.current);
      widgetIdRef.current = undefined;
    };
  }, [container]);

  const getToken = async (): Promise<string> => {
    await readyRef.current;
    if (!widgetIdRef.current || !window.turnstile) {
      throw new Error("Captcha widget failed to load");
    }
    return new Promise((resolve, reject) => {
      pendingRef.current = { resolve, reject };
      window.turnstile!.reset(widgetIdRef.current);
      window.turnstile!.execute(widgetIdRef.current!);
    });
  };

  return { containerRef, getToken };
}
